import {createApi} from '@reduxjs/toolkit/query/react';

import {toFinancialProductPreferenceRequestBody} from 'features/financial-products';

import {getOnlineOfferDataSearch} from '../utils/getOnlineOfferDataSearch';
import {getPdfQueryParams} from '../utils/getPdfQueryParams';
import {dmsBaseQuery} from './dmsBaseQuery';
import {
  GetBusinessCaseOnlineOfferApiArg,
  GetBusinessCaseOnlineOfferApiResponse,
  GetOnlineOfferFinancialProductsApiArg,
  GetOnlineOfferFinancialProductsApiResponse,
  GetPdfByOfferIdApiArg,
  GetPdfByOfferIdApiResponse,
  ListLogosApiArg,
  ListLogosApiResponse,
  PublicFinancialProductsResponseBodySchema,
  SendOnlineOfferEngagementApiArg,
  UpdateFinancialProductPreferenceApiArg,
  UpdateFinancialProductPreferenceApiResponse,
} from './types/onlineOfferTypes';

// See PublicFinancialProductsResponseBodySchema for the public-payload privacy invariant.
const toPublicFinancialProducts = (body: unknown) =>
  PublicFinancialProductsResponseBodySchema.strip().parse(body);

const ONLINE_OFFER_TAG = 'OnlineOffer';

const toPriceKey = (price: {amount: string; currency: string} | null | undefined) =>
  price ? `${price.amount} ${price.currency}` : null;

export const onlineOfferApi = createApi({
  baseQuery: dmsBaseQuery,
  reducerPath: 'onlineOfferApi',
  tagTypes: [ONLINE_OFFER_TAG],
  endpoints: (build) => ({
    getBusinessCaseOnlineOffer: build.query<
      GetBusinessCaseOnlineOfferApiResponse,
      GetBusinessCaseOnlineOfferApiArg
    >({
      query: (queryArg) => {
        const paramsToSet = getOnlineOfferDataSearch(queryArg);

        return {
          url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/data${paramsToSet ? `?${paramsToSet}` : ''}`,
          headers: {'X-Workspace': queryArg.workspace},
        };
      },
      providesTags: [ONLINE_OFFER_TAG],
    }),
    /** Products are polled separately because current configuration and price can invalidate them. */
    getOnlineOfferFinancialProducts: build.query<
      GetOnlineOfferFinancialProductsApiResponse,
      GetOnlineOfferFinancialProductsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/financial-products`,
        headers: {
          'X-Workspace': queryArg.workspace,
          'Accept-Language': queryArg.language,
        },
      }),
      transformResponse: toPublicFinancialProducts,
      /**
       * Re-read `/data` once when the polled pricing basis changes; in-flight reads remain fresher.
       */
      async onQueryStarted(arg, {dispatch, getState, getCacheEntry, queryFulfilled}) {
        const previouslyPolledPrice = toPriceKey(getCacheEntry().data?.vehiclePriceWithVat);

        try {
          const {data} = await queryFulfilled;
          const polledPrice = toPriceKey(data.vehiclePriceWithVat);

          if (polledPrice === previouslyPolledPrice) {
            return;
          }

          // Preserve the session and opener carried by the cached `/data` request.
          const loadedOffer = onlineOfferApi.util
            .selectCachedArgsForQuery(getState(), 'getBusinessCaseOnlineOffer')
            .map((originalArgs) =>
              onlineOfferApi.endpoints.getBusinessCaseOnlineOffer.select(originalArgs)(getState())
            )
            .find((read) => read.originalArgs?.offerId === arg.offerId);

          if (
            loadedOffer?.status !== 'fulfilled' ||
            polledPrice === toPriceKey(loadedOffer.data?.vehiclePriceWithVat)
          ) {
            return;
          }

          dispatch(onlineOfferApi.util.invalidateTags([ONLINE_OFFER_TAG]));
        } catch {
          // A failed poll says nothing about the price; the surface reports it.
        }
      },
    }),
    /**
     * The full priced response is the quote, so the frontend never assembles its own.
     * A stale write response must not replace a frozen snapshot or later reopened read.
     */
    updateFinancialProductPreference: build.mutation<
      UpdateFinancialProductPreferenceApiResponse,
      UpdateFinancialProductPreferenceApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/financial-product-preference`,
        method: 'PUT',
        headers: {
          'X-Workspace': queryArg.workspace,
          'Accept-Language': queryArg.language,
        },
        body: toFinancialProductPreferenceRequestBody(
          queryArg.coordinates,
          queryArg.expectedPreferenceRevision
        ),
      }),
      transformResponse: toPublicFinancialProducts,
    }),
    listLogos: build.query<ListLogosApiResponse, ListLogosApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/logo`,
        headers: {'X-Workspace': queryArg['X-Workspace']},
      }),
    }),
    getPdfByOfferId: build.query<GetPdfByOfferIdApiResponse, GetPdfByOfferIdApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/pdf`,
        params: getPdfQueryParams(queryArg),
        headers: {
          'X-Workspace': queryArg.workspace,
        },
      }),
    }),
    sendOnlineOfferEngagement: build.mutation<void, SendOnlineOfferEngagementApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/engagement`,
        method: 'POST',
        headers: {'X-Workspace': queryArg.workspace},
        body: {
          sessionId: queryArg.sessionId,
          activeTimeMs: queryArg.activeTimeMs,
          scrollDepth: queryArg.scrollDepth,
          certificateClick: queryArg.certificateClick,
        },
      }),
    }),
  }),
});

export const {
  useGetBusinessCaseOnlineOfferQuery,
  useGetOnlineOfferFinancialProductsQuery,
  useUpdateFinancialProductPreferenceMutation,
  useListLogosQuery,
  useLazyGetPdfByOfferIdQuery,
  useSendOnlineOfferEngagementMutation,
} = onlineOfferApi;
